import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import './ContactPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ContactPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const showBackButton = location.state?.fromDealerLocator;
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const [loading, setLoading] = useState(false);

  const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const envApiUrl = process.env.REACT_APP_API_URL?.trim();
  const API_URL = envApiUrl && (isLocalHost || (!envApiUrl.includes("localhost") && !envApiUrl.includes("127.0.0.1"))) ? envApiUrl : '/api';

  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/settings/global`);
        const json = await res.json();
        if (json.success && json.data) {
          setSettings(json.data);
        }
      } catch (err) {
        console.error('Failed to load global settings in contact page:', err);
      }
    };
    fetchSettings();
  }, [API_URL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/leads/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: form.message
        })
      });
      if (response.ok) {
        alert(t('contact.form.success'));
        setForm({ name: '', email: '', phone: '', message: '' });
      } else {
        alert(t('contact.form.fail'));
      }
    } catch (error) {
      alert(t('contact.form.error'));
    } finally {
      setLoading(false);
    }
  };

  const businessName = settings?.businessName || 'Kisan Tradecom Private Limited';
  const wholesalePhone = settings?.wholesalePhone || '+91 96312 77264';
  const retailPhone = settings?.retailPhone || '+91 95236 17526';
  const sparesPhone = settings?.sparesPhone || '+91 96312 77146';
  const gemPhone = settings?.gemPhone || '+91 95234 48810';
  const supportEmail = settings?.supportEmail || 'ktpldsbsales@gmail.com';
  const sparesEmail = settings?.sparesEmail || 'ktpldsbspares@gmail.com';
  const retailEmail = settings?.retailEmail || 'Kisan.mahesh1972@gmail.com';
  const officeAddress = settings?.officeAddress;
  const businessHours = settings?.businessHours;

  const facebookUrl = settings?.socialLinks?.facebook || 'https://www.facebook.com/share/18qJ5U9PYE/';
  const instagramUrl = settings?.socialLinks?.instagram || 'https://www.instagram.com/kisanengineeringworks1?igsh=MW11YTJ2aDE1endzeQ==';
  const youtubeUrl = settings?.socialLinks?.youtube || 'https://youtube.com/@kisanengineeringworks1?si=spWrGg-fYZBqb8Q_';

  return (
    <div className="contact-page">
      <Header />

      {/* Green top bar */}
      <div className="contact-top-bar"></div>

      {/* Page Title */}
      <section className="contact-hero">
        <div className="container" style={{ position: 'relative' }}>
          {showBackButton && (
            <button className="contact-back-btn" onClick={() => navigate('/dealer-locator')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              <span className="contact-back-btn-text">{t('contact.back_to_dealer', 'Back to Dealer Locator')}</span>
            </button>
          )}
          <h1 className="contact-title">{t('contact.title')}</h1>
          <p className="contact-subtitle">{t('contact.subtitle')}</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="contact-main">
        <div className="container">
          <div className="contact-grid">

            {/* Left — Head Office Info */}
            <div className="contact-info-card">
              <h2 className="card-heading">{t('contact.head_office')}</h2>

              <div className="info-row">
                <div className="info-icon green">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                </div>
                <div>
                  <strong>{businessName}</strong>
                  <a 
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(businessName + ' ' + (officeAddress || 'Bela Industrial Estate Muzaffarpur'))}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="address-map-link"
                    title="View on Google Maps"
                  >
                    {officeAddress ? (
                      <p className="address-line" style={{ whiteSpace: 'pre-line' }}>{officeAddress}</p>
                    ) : (
                      <>
                        <p className="address-line">{t('contact.address_l1')}</p>
                        <p className="address-line">{t('contact.address_l2')}</p>
                      </>
                    )}
                  </a>
                </div>
              </div>

              <div className="info-row">
                <div className="info-icon orange">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l2.27-2.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                </div>
                <div>
                  <div style={{ marginBottom: '8px' }}>
                    <a href={`tel:${wholesalePhone}`} className="info-highlight" style={{ textDecoration: 'none' }}>
                      {wholesalePhone} <span style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: 'bold', textTransform: 'none' }}>(Wholesale)</span>
                    </a>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <a href={`tel:${retailPhone}`} className="info-highlight" style={{ textDecoration: 'none' }}>
                      {retailPhone} <span style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: 'bold', textTransform: 'none' }}>(Retail)</span>
                    </a>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <a href={`tel:${sparesPhone}`} className="info-highlight" style={{ textDecoration: 'none' }}>
                      {sparesPhone} <span style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: 'bold', textTransform: 'none' }}>(Spares)</span>
                    </a>
                  </div>
                  <div>
                    <a href={`tel:${gemPhone}`} className="info-highlight" style={{ textDecoration: 'none' }}>
                      {gemPhone} <span style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: 'bold', textTransform: 'none' }}>(GEM)</span>
                    </a>
                  </div>
                </div>
              </div>

              <div className="info-row">
                <div className="info-icon orange">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                </div>
                <div>
                  <div style={{ marginBottom: '8px' }}>
                    <a href={`mailto:${supportEmail}`} className="info-highlight" style={{ textDecoration: 'none' }}>
                      {supportEmail} <span style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: 'bold', textTransform: 'none' }}>(Wholesale / Sales)</span>
                    </a>
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <a href={`mailto:${sparesEmail}`} className="info-highlight" style={{ textDecoration: 'none' }}>
                      {sparesEmail} <span style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: 'bold', textTransform: 'none' }}>(Spares Parts)</span>
                    </a>
                  </div>
                  <div>
                    <a href={`mailto:${retailEmail}`} className="info-highlight" style={{ textDecoration: 'none' }}>
                      {retailEmail} <span style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: 'bold', textTransform: 'none' }}>(Retail)</span>
                    </a>
                  </div>
                </div>
              </div>

              <div className="info-row">
                <div className="info-icon green">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </div>
                <div>
                  <strong>{t('contact.hours')}</strong>
                  {businessHours ? (
                    <p style={{ whiteSpace: 'pre-line' }}>{businessHours}</p>
                  ) : (
                    <>
                      <p>{t('contact.mon_sat')}</p>
                      <p>{t('contact.sunday')}</p>
                    </>
                  )}
                </div>
              </div>

              <div className="info-row">
                <div className="info-icon orange">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="18" cy="5" r="3"></circle>
                    <circle cx="6" cy="12" r="3"></circle>
                    <circle cx="18" cy="19" r="3"></circle>
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                  </svg>
                </div>
                <div>
                  <strong>{t('contact.social_title', 'Connect with us')}</strong>
                  <div className="social-links-horizontal">
                    <a href={instagramUrl} className="social-link-item instagram" target="_blank" rel="noopener noreferrer" title="Instagram">
                      <div className="social-link-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                        </svg>
                      </div>
                    </a>
                    <a href={facebookUrl} className="social-link-item facebook" target="_blank" rel="noopener noreferrer" title="Facebook">
                      <div className="social-link-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                        </svg>
                      </div>
                    </a>
                    <a href={youtubeUrl} className="social-link-item youtube" target="_blank" rel="noopener noreferrer" title="YouTube">
                      <div className="social-link-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"></path>
                          <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02"></polygon>
                        </svg>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right — Send Message Form */}
            <div className="contact-form-card">
              <h2 className="card-heading">{t('contact.send_message')}</h2>
              <form onSubmit={handleSubmit} className="contact-form">
                <input
                  type="text"
                  name="name"
                  placeholder={t('contact.form.name')}
                  value={form.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
                <input
                  type="email"
                  name="email"
                  placeholder={t('contact.form.email')}
                  value={form.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder={t('contact.form.phone')}
                  value={form.phone}
                  onChange={handleChange}
                  className="form-input"
                />
                <textarea
                  name="message"
                  placeholder={t('contact.form.message')}
                  value={form.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="form-textarea"
                ></textarea>
                <button type="submit" className="btn-send" disabled={loading}>
                  {loading ? t('contact.form.sending') : t('contact.form.send')}
                </button>
              </form>
            </div>

          </div>
        </div>
      </section>


      {/* WhatsApp FAB */}
      <div className="whatsapp-fab" onClick={() => window.open('https://wa.me/919631277264', '_blank')} style={{ cursor: 'pointer' }}>
        <svg viewBox="0 0 24 24" width="32" height="32" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.438 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.414 0 .004 5.412 0 12.046c0 2.123.555 4.195 1.608 6.014L0 24l6.117-1.605a11.782 11.782 0 005.925 1.587h.005c6.632 0 12.043-5.414 12.047-12.048a11.821 11.821 0 00-3.526-8.513"/>
        </svg>
      </div>

      <Footer />
    </div>
  );
};

export default ContactPage;


